import React, { useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import OnboardingRestaurante from "../paginas/OnboardingRestaurante";
import OnboardingAdminLimitado from "../paginas/OnboardingAdminLimitado";
import RestaurantLifecyclePanel from "./RestaurantLifecyclePanel";
import RestaurantContractPriority from "./RestaurantContractPriority";
import { getRestaurantProfile } from "../../services/local/restaurantProfile";
import { buildRestaurantBrandTheme } from "../../services/local/brandTheme";
import { applyInstitutionalBrandingAssets } from "../../services/local/institutionalBranding";
import {
  getMerchantEntryContinuation,
  withMerchantEntryContinuation,
} from "../../services/local/merchantEntryNavigation";
import {
  canOpenBackofficeOnCurrentHost,
  isInternalBackofficeHost,
  isLocalRuntimeHost,
} from "../../services/local/runtimeHost";

const loadingLogoKeyframes = `
  @keyframes mozzoapp-loading-spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  @keyframes mozzoapp-loading-pulse {
    0%, 100% { transform: scale(0.92); opacity: 0.82; }
    50% { transform: scale(1.04); opacity: 1; }
  }

  @keyframes mozzoapp-loading-float {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-8px); }
  }
`;

const fullScreenStyles = {
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "24px",
  backgroundColor: "#f8fafc",
};

const cardStyles = {
  width: "100%",
  maxWidth: "460px",
  backgroundColor: "#ffffff",
  borderRadius: "16px",
  padding: "28px",
  boxShadow: "0 18px 40px rgba(15, 23, 42, 0.08)",
  border: "1px solid #e2e8f0",
  textAlign: "center",
};

function AccessRedirect({ to, resumeMerchant = false }) {
  const continuation = getMerchantEntryContinuation(useLocation());
  const destination = resumeMerchant && to === "/app" && continuation
    ? continuation
    : withMerchantEntryContinuation(to, continuation);
  return <Navigate to={destination} replace />;
}

function ProtectedRoute({
  children,
  allowAccessSelection = false,
  allowPlatformBackoffice = false,
  waiterOnly = false,
  kitchenOnly = false,
}) {
  const {
    session,
    loadingAuth,
    loadingWorkspace,
    authError,
    needsWorkspaceSetup,
    missingBranchAccess,
    needsAccessSelection,
    needsUserProfileSetup,
    isWaiterWorkspace,
    isKitchenWorkspace,
    canAccessModule,
    accessMode,
    isAdminGeneral,
    isPlatformBackofficeAdmin,
    currentBranch,
    isCreatorMode,
    isSupabaseConfigured,
    currentRestaurant,
    currentCompany,
    user,
    refreshWorkspace,
    signOut,
  } = useAuth();

  const canUseStandaloneKitchen =
    isKitchenWorkspace ||
    (canAccessModule("cocina") &&
      Boolean(currentBranch?.id) &&
      (!isAdminGeneral || accessMode === "branch"));
  const kitchenRedirectPath = isKitchenWorkspace ? "/cocina" : "/app";
  const canUseLocalBackoffice =
    allowPlatformBackoffice && isLocalRuntimeHost();

  const brandTheme = buildRestaurantBrandTheme(
    getRestaurantProfile(currentRestaurant)
  );
  const loadingLogoUrl = brandTheme.effectiveLoadingLogoUrl || "";
  const loadingLogoAnimation =
    brandTheme.loadingAnimation === "pulse"
      ? { animation: "mozzoapp-loading-pulse 1.45s ease-in-out infinite" }
      : brandTheme.loadingAnimation === "float"
        ? { animation: "mozzoapp-loading-float 1.9s ease-in-out infinite" }
        : { animation: "mozzoapp-loading-spin 1.8s linear infinite" };

  useEffect(() => {
    if (currentRestaurant?.id) {
      return;
    }

    applyInstitutionalBrandingAssets();
  }, [session, loadingAuth, loadingWorkspace, accessMode, currentBranch?.id, currentRestaurant?.id]);

  if (!isSupabaseConfigured && !isCreatorMode) {
    return <AccessRedirect to="/login" />;
  }

  // Keep the onboarding form mounted while its atomic registration is being submitted.
  if (loadingAuth || (session && loadingWorkspace && !needsWorkspaceSetup)) {
    if (loadingLogoUrl) {
      return (
        <div style={fullScreenStyles}>
          <style>{loadingLogoKeyframes}</style>
          <img
            src={loadingLogoUrl}
            alt="Logo de carga"
            style={{
              ...loadingLogoStyles,
              ...loadingLogoAnimation,
            }}
          />
        </div>
      );
    }

    return (
      <div style={fullScreenStyles}>
        <style>{loadingLogoKeyframes}</style>
        <div style={cardStyles}>
          <>
            <h2 style={{ marginTop: 0 }}>Preparando tu panel</h2>
            <p style={{ marginBottom: 0, color: "#475569" }}>
              Estamos verificando tu acceso y cargando la empresa y la sucursal activa.
            </p>
          </>
        </div>
      </div>
    );
  }

  if (!session) {
    return <AccessRedirect to="/login" />;
  }

  if (currentCompany?.status === "cancelled" && !isCreatorMode && !allowPlatformBackoffice) {
    return <RestaurantLifecyclePanel companyId={currentCompany.id} showContract
      onChanged={() => refreshWorkspace(user)} onSignOut={() => signOut()} />;
  }

  if (needsWorkspaceSetup && !isCreatorMode) {
    return <OnboardingRestaurante key={user?.id || session.user?.id} />;
  }

  if (needsUserProfileSetup && !isCreatorMode) {
    return <OnboardingAdminLimitado />;
  }

  if (
    allowPlatformBackoffice &&
    !isPlatformBackofficeAdmin &&
    !canUseLocalBackoffice
  ) {
    if (isInternalBackofficeHost()) {
      return (
        <div style={fullScreenStyles}>
          <div style={cardStyles}>
            <h2 style={{ marginTop: 0 }}>Sin acceso al Backoffice</h2>
            <p style={{ color: "#475569", marginBottom: 0 }}>
              Esta cuenta no tiene permisos para abrir el panel interno de MozzoApp.
            </p>
          </div>
        </div>
      );
    }

    if (needsAccessSelection && !isCreatorMode) {
      return <AccessRedirect to="/acceso" />;
    }

    if (isWaiterWorkspace) {
      return <Navigate to="/mozo" replace />;
    }

    if (isKitchenWorkspace) {
      return <Navigate to="/cocina" replace />;
    }

    return <Navigate to="/app" replace />;
  }

  if (allowPlatformBackoffice && !canOpenBackofficeOnCurrentHost()) {
    if (isWaiterWorkspace) {
      return <Navigate to="/mozo" replace />;
    }

    if (isKitchenWorkspace) {
      return <Navigate to="/cocina" replace />;
    }

    return <Navigate to="/app" replace />;
  }

  if (needsAccessSelection && !isCreatorMode) {
    if (
      allowPlatformBackoffice &&
      (isPlatformBackofficeAdmin || canUseLocalBackoffice)
    ) {
      return children;
    }

    if (allowAccessSelection) {
      return children;
    }

    return <AccessRedirect to="/acceso" />;
  }

  if (allowAccessSelection && !needsAccessSelection) {
    return (
      <AccessRedirect
        to={isWaiterWorkspace ? "/mozo" : kitchenRedirectPath}
        resumeMerchant
      />
    );
  }

  if (waiterOnly && !isWaiterWorkspace) {
    return <Navigate to={kitchenRedirectPath} replace />;
  }

  if (kitchenOnly && !canUseStandaloneKitchen) {
    if (isWaiterWorkspace) {
      return <Navigate to="/mozo" replace />;
    }

    if (isAdminGeneral && accessMode !== "branch") {
      return <AccessRedirect to="/acceso" />;
    }

    return <Navigate to="/app" replace />;
  }

  if (!waiterOnly && !kitchenOnly && isWaiterWorkspace) {
    return <Navigate to="/mozo" replace />;
  }

  if (!waiterOnly && !kitchenOnly && isKitchenWorkspace) {
    return <Navigate to="/cocina" replace />;
  }

  if (missingBranchAccess && !isCreatorMode) {
    return (
      <div style={fullScreenStyles}>
        <div style={cardStyles}>
          <h2 style={{ marginTop: 0 }}>Sin sucursal asignada</h2>
          <p style={{ color: "#475569", marginBottom: 0 }}>
            Tu cuenta ingreso correctamente, pero todavia no tiene una sucursal habilitada.
            Necesitas que un admin general te asigne acceso antes de usar el panel.
          </p>
        </div>
      </div>
    );
  }

  if (authError) {
    return (
      <div style={fullScreenStyles}>
        <div style={cardStyles}>
          <h2 style={{ marginTop: 0 }}>No pudimos abrir tu panel</h2>
          <p style={{ color: "#475569" }}>{authError}</p>
        </div>
      </div>
    );
  }

  if (currentCompany?.id && isAdminGeneral && !isCreatorMode && !allowPlatformBackoffice && !allowAccessSelection) {
    return <RestaurantContractPriority key={`${currentCompany.id}:${user?.id}`} companyId={currentCompany.id}
      onSignOut={() => signOut()} onWorkspaceChanged={() => refreshWorkspace(user)}>{children}</RestaurantContractPriority>;
  }
  return children;
}

const loadingLogoStyles = {
  width: "auto",
  height: "auto",
  maxWidth: "35vw",
  maxHeight: "35vh",
  objectFit: "contain",
  display: "block",
};

export default ProtectedRoute;
